// src/data/schemas/Incident.js
// Structured operational incidents — distinct from UserReport (feed posts).
//
// The distinction:
//   UserReport — a snapshot observation ("water on Chef Menteur at 2pm")
//   Incident   — a tracked operational event with lifecycle, geometry,
//                updates, resources, and affected area
//
// Incidents can be:
//   • Ingested automatically (511LA, USGS gauge inference, NWS derived)
//   • Escalated from a UserReport when confirmations cross a threshold
//   • Created directly by official sources (DOTD, fire department, etc.)
//
// Firestore layout:
//   incidents/{incidentId}                     — root document
//   incidents/{incidentId}/updates/{updateId}  — timeline subcollection
//   incidents/{incidentId}/confirmations/{uid} — per-user confirmation

import {
  uuid, nowISO, isLat, isLon, isISO, isIn, isNum, isStr,
  runValidation, toFirestoreClean, fromFirestoreNormalize,
} from './shared';
import { geohashForReport } from '@services/geolocation/geohash';

// ── Enums ──────────────────────────────────────────────────────────
import { SOURCE_TIER } from './Alert';
export { SOURCE_TIER };

export const INCIDENT_STATUS = [
  'unverified',   // single report, not yet confirmed
  'active',       // confirmed, ongoing
  'monitoring',   // exists, below action threshold
  'mitigating',   // responders on scene
  'resolved',     // no longer a hazard
  'false_alarm',  // confirmed incorrect
];

export const INCIDENT_TYPE = [
  // sourceTier field uses SOURCE_TIER enum from Alert.js
  'road_flooding',
  'road_closure',
  'bridge_closure',
  'power_outage',
  'power_restored',
  'water_main',
  'gas_leak',
  'structure_fire',
  'grass_fire',
  'hazmat',
  'debris',
  'accident',
  'structure_damage',
  'levee_concern',
  'pump_station',
  'curfew',
  'evacuation_order',
  'evacuation_warning',
  'shelter_activation',
  'other',
];

export const INCIDENT_SOURCE = [
  'dotd_511la',
  'usgs_inference',
  'nws_derived',
  'entergy',
  'gohsep',
  'community',         // escalated from UserReport confirmations
  'official_agency',   // direct from responder
];

export const EVACUATION_STATUS = [
  'none',
  'voluntary',        // voluntary evacuation suggested
  'warning',          // prepare to evacuate
  'mandatory',        // mandatory evacuation order
  'return_allowed',   // all-clear, return permitted
];

export const FLOOD_RISK = ['extreme', 'high', 'moderate', 'low', 'none'];

export const RESOURCE_TYPE = [
  'fire',
  'police',
  'ems',
  'public_works',
  'coast_guard',
  'national_guard',
  'red_cross',
  'utility_crew',
  'volunteers',
  'other',
];

// ── Geometry types ────────────────────────────────────────────────
// Incidents can have point, radius, or polygon geometry.
// Stored as a plain JS object — not GeoJSON (keeps Firestore reads simple).
//
// { type: 'point',   lat, lon }
// { type: 'radius',  lat, lon, radiusM }
// { type: 'polygon', coordinates: [[lat,lon], ...] }
// { type: 'corridor',start: [lat,lon], end: [lat,lon], widthM }

// ── Schema ─────────────────────────────────────────────────────────
export const SCHEMA_INCIDENT = {
  incidentId:        { required: true,  type: 'string' },
  type:              { required: true,  type: 'string', check: (v) => isIn(v, INCIDENT_TYPE),   message: `one of: ${INCIDENT_TYPE.join(', ')}` },
  status:            { required: true,  type: 'string', check: (v) => isIn(v, INCIDENT_STATUS), message: `one of: ${INCIDENT_STATUS.join(', ')}` },
  source:            { required: true,  type: 'string', check: (v) => isIn(v, INCIDENT_SOURCE), message: `one of: ${INCIDENT_SOURCE.join(', ')}` },
  severity:          { required: true,  type: 'number', min: 1, max: 5 },
  title:             { required: true,  type: 'string', maxLen: 200 },
  description:       { required: false, type: 'string', maxLen: 2000 },

  // ── Geometry ────────────────────────────────────────────────────
  // At minimum one of: geometry.lat+lon, roadName, or affectedAreas
  geometry:          { required: false, type: 'object' },   // see geometry types above
  // Geohash of the primary point (geometry.lat/lon or roadName centroid)
  geohash:           { required: false, type: 'string' },

  // ── Affected areas ──────────────────────────────────────────────
  // Named zones, parishes, or cross streets impacted
  affectedAreas: { required: false, type: 'array' },  // string[]
  parish:        { required: false, type: 'string', maxLen: 100 },
  zone:          { required: false, type: 'string', maxLen: 100 },

  // ── Road specifics ───────────────────────────────────────────────
  roadName:          { required: false, type: 'string', maxLen: 200 },
  crossStreet:       { required: false, type: 'string', maxLen: 200 },
  direction:         { required: false, type: 'string', maxLen: 50 },
  lanesClosed:       { required: false, type: 'number', min: 0, max: 20 },
  lanesTotal:        { required: false, type: 'number', min: 0, max: 20 },
  isBothDirections:  { required: false, type: 'boolean' },

  // ── Flood specifics ──────────────────────────────────────────────
  floodRisk:         { required: false, type: 'string', check: (v) => isIn(v, FLOOD_RISK) },
  waterDepthIn:      { required: false, type: 'number', min: 0, max: 300 },
  usgsGaugeId:       { required: false, type: 'string' },
  usgsGaugeFt:       { required: false, type: 'number' },

  // ── Power specifics ──────────────────────────────────────────────
  estimatedCustomers:  { required: false, type: 'number', min: 0 },
  estimatedRestoreAt:  { required: false, type: 'string', check: isISO },

  // ── Evacuation status ────────────────────────────────────────────
  evacuationStatus:  {
    required: false,
    type: 'string',
    check: (v) => isIn(v, EVACUATION_STATUS),
    message: `one of: ${EVACUATION_STATUS.join(', ')}`,
  },
  evacuationZones:   { required: false, type: 'array' },   // string[] of zone names
  evacuationOrder:   { required: false, type: 'string', maxLen: 500 },  // official order text

  // ── Confidence (set by intelligence engine after scoring) ─────────
  confidence:        { required: false, type: 'number', min: 0, max: 1 },
  confidenceLevel:   { required: false, type: 'string' },  // confirmed|likely|developing|unverified|stale
  confidenceLabel:   { required: false, type: 'string' },
  evidenceLabels:    { required: false, type: 'array' },
  isStale:           { required: false, type: 'boolean' },
  isDisputed:        { required: false, type: 'boolean' },
  scoredAt:          { required: false, type: 'string', check: isISO },

  // ── Confirmations (denormalized count — subcollection has full records) ──
  confirmCount:      { required: false, type: 'number', min: 0 },
  denyCount:         { required: false, type: 'number', min: 0 },

  // ── Resources on scene ───────────────────────────────────────────
  // Denormalized summary — full detail in resources subcollection
  resourceCount:     { required: false, type: 'number', min: 0 },
  resourceTypes:     { required: false, type: 'array' },  // RESOURCE_TYPE[]

  // ── Origin ───────────────────────────────────────────────────────
  // If escalated from a UserReport
  originReportId:    { required: false, type: 'string' },
  // External source IDs
  dotdIncidentId:    { required: false, type: 'string' },
  nwsAlertId:        { required: false, type: 'string' },
  entergyIncidentId: { required: false, type: 'string' },

  // ── Timestamps ───────────────────────────────────────────────────
  reportedAt:        { required: true,  type: 'string', check: isISO },
  updatedAt:         { required: false, type: 'string', check: isISO },
  resolvedAt:        { required: false, type: 'string', check: isISO },
  estimatedDuration: { required: false, type: 'number', min: 0 },  // minutes
};

// ── Subcollection schemas ─────────────────────────────────────────

// Update record — appended to incidents/{id}/updates
export const SCHEMA_INCIDENT_UPDATE = {
  updateId:   { required: true,  type: 'string' },
  incidentId: { required: true,  type: 'string' },
  authorId:   { required: false, type: 'string' },
  authorName: { required: false, type: 'string', maxLen: 100 },
  isOfficial: { required: false, type: 'boolean' },           // from responder / agency
  text:       { required: true,  type: 'string', maxLen: 1000 },
  // Optional delta — what changed
  statusChange:     { required: false, type: 'string', check: (v) => isIn(v, INCIDENT_STATUS) },
  evacuationChange: { required: false, type: 'string', check: (v) => isIn(v, EVACUATION_STATUS) },
  severityChange:   { required: false, type: 'number', min: 1, max: 5 },
  newWaterDepthIn:  { required: false, type: 'number', min: 0, max: 300 },
  mediaUrls:        { required: false, type: 'array' },
  createdAt:        { required: true,  type: 'string', check: isISO },
};

// Confirmation record — written to incidents/{id}/confirmations/{userId}
export const SCHEMA_INCIDENT_CONFIRMATION = {
  userId:     { required: true,  type: 'string' },
  incidentId: { required: true,  type: 'string' },
  vote:       { required: true,  type: 'string', check: (v) => isIn(v, ['confirm', 'deny']) },
  lat:        { required: false, type: 'number', check: isLat },
  lon:        { required: false, type: 'number', check: isLon },
  createdAt:  { required: true,  type: 'string', check: isISO },
};

// Resource assignment — written to incidents/{id}/resources
export const SCHEMA_INCIDENT_RESOURCE = {
  resourceId:  { required: true,  type: 'string' },
  incidentId:  { required: true,  type: 'string' },
  type:        { required: true,  type: 'string', check: (v) => isIn(v, RESOURCE_TYPE) },
  unit:        { required: false, type: 'string', maxLen: 100 },   // "Engine 3", "NOPD District 7"
  status:      { required: true,  type: 'string', check: (v) => isIn(v, ['dispatched','on_scene','cleared']) },
  dispatchedAt:{ required: false, type: 'string', check: isISO },
  arrivedAt:   { required: false, type: 'string', check: isISO },
  clearedAt:   { required: false, type: 'string', check: isISO },
};

// ── Factories ──────────────────────────────────────────────────────
export function createIncident(overrides = {}) {
  const type     = overrides.type ?? 'other';
  const severity = overrides.severity ?? _defaultSeverity(type);
  return {
    incidentId:          uuid(),
    type,
    status:              'unverified',
    source:              'community',
    severity,
    title:               '',
    description:         null,
    geometry:            null,
    geohash:             null,
    affectedAreas:       [],
    parish:              null,
    zone:                null,
    roadName:            null,
    crossStreet:         null,
    direction:           null,
    lanesClosed:         null,
    lanesTotal:          null,
    isBothDirections:    false,
    floodRisk:           null,
    waterDepthIn:        null,
    usgsGaugeId:         null,
    usgsGaugeFt:         null,
    estimatedCustomers:  null,
    estimatedRestoreAt:  null,
    evacuationStatus:    'none',
    evacuationZones:     [],
    evacuationOrder:     null,
    confidence:          null,
    confidenceLevel:     null,
    confidenceLabel:     null,
    evidenceLabels:      [],
    isStale:             false,
    isDisputed:          false,
    scoredAt:            null,
    confirmCount:        0,
    denyCount:           0,
    resourceCount:       0,
    resourceTypes:       [],
    originReportId:      null,
    dotdIncidentId:      null,
    nwsAlertId:          null,
    entergyIncidentId:   null,
    reportedAt:          nowISO(),
    updatedAt:           null,
    resolvedAt:          null,
    activatedAt:         null,
    monitoringAt:        null,
    mitigatingAt:        null,
    falseAlarmAt:        null,
    expiredAt:           null,
    archivedAt:          null,
    transitionReason:    null,
    mergedIntoId:        null,
    mergedReportIds:     [],
    estimatedDuration:   null,
    ...overrides,
    severity, // enforce derived severity
    // Compute geohash from geometry if provided
    geohash: overrides.geohash ?? (overrides.geometry?.lat && overrides.geometry?.lon ? geohashForReport(overrides.geometry.lat, overrides.geometry.lon) : null),
  };
}

export function createIncidentUpdate(incidentId, overrides = {}) {
  return {
    updateId:        uuid(),
    incidentId,
    authorId:        null,
    authorName:      'Anonymous',
    isOfficial:      false,
    text:            '',
    statusChange:    null,
    evacuationChange:null,
    severityChange:  null,
    newWaterDepthIn: null,
    mediaUrls:       [],
    createdAt:       nowISO(),
    ...overrides,
  };
}

export function createConfirmation(incidentId, userId, vote) {
  return {
    userId,
    incidentId,
    vote,
    lat:       null,
    lon:       null,
    createdAt: nowISO(),
  };
}

export function createResource(incidentId, overrides = {}) {
  return {
    resourceId:   uuid(),
    incidentId,
    type:         'other',
    unit:         null,
    status:       'dispatched',
    dispatchedAt: nowISO(),
    arrivedAt:    null,
    clearedAt:    null,
    ...overrides,
  };
}

// ── Validation ─────────────────────────────────────────────────────
export function validateIncident(obj) {
  const result = runValidation(obj, SCHEMA_INCIDENT);
  // Must have geometry OR roadName OR affectedAreas
  const hasGeom   = obj.geometry?.lat || obj.geometry?.coordinates?.length;
  const hasRoad   = isStr(obj.roadName ?? '');
  const hasAreas  = (obj.affectedAreas ?? []).length > 0;
  if (!hasGeom && !hasRoad && !hasAreas) {
    result.errors.push('location: must provide geometry, roadName, or affectedAreas');
    result.valid = false;
  }
  return result;
}

export function validateIncidentUpdate(obj) {
  return runValidation(obj, SCHEMA_INCIDENT_UPDATE);
}

// ── Ingestion factories ────────────────────────────────────────────
// incidentFrom511: maps a raw 511LA event → Incident
export function incidentFrom511(event) {
  const raw  = (event.EventType ?? event.eventType ?? '').toLowerCase();
  const type = raw.includes('flood')        ? 'road_flooding'
             : raw.includes('clos')         ? 'road_closure'
             : raw.includes('accident')     ? 'accident'
             : raw.includes('construction') ? 'debris'
             : raw.includes('hazmat')       ? 'hazmat'
             : raw.includes('power')        ? 'power_outage'
             : raw.includes('bridge')       ? 'bridge_closure'
             : 'other';

  const lat = parseFloat(event.Latitude  ?? event.latitude  ?? '') || null;
  const lon = parseFloat(event.Longitude ?? event.longitude ?? '') || null;

  return createIncident({
    incidentId:    '511-' + (event.id ?? event.Id ?? uuid()),
    type,
    status:        raw.includes('clear') ? 'resolved' : 'active',
    source:        'dotd_511la',
    title:         event.RoadwayName ?? event.road ?? event.roadwayName ?? 'Road Incident',
    description:   (event.Description ?? event.description ?? '').substring(0, 2000),
    geometry:      lat && lon ? { type: 'point', lat, lon } : null,
    affectedAreas: _extractAreas(event),
    roadName:      event.RoadwayName ?? event.road ?? event.roadwayName ?? null,
    parish:        _extractParish511(event),
    reportedAt:    nowISO(),
    dotdIncidentId:String(event.id ?? event.Id ?? ''),
  });
}

// incidentFromGauge: infers road flooding from USGS gauge reading
export function incidentFromGauge(crossing, gaugeFt) {
  const flooded = gaugeFt >= crossing.closureStage;
  return createIncident({
    incidentId:   'usgs-' + crossing.id,
    type:         flooded ? 'road_flooding' : 'road_closure',
    status:       flooded ? 'active' : 'monitoring',
    source:       'usgs_inference',
    severity:     flooded ? 4 : 2,
    title:        crossing.name,
    description:  flooded
      ? `Gauge reading ${gaugeFt.toFixed(1)}ft — above ${crossing.closureStage}ft closure threshold. Road likely flooded.`
      : `Gauge reading ${gaugeFt.toFixed(1)}ft — monitoring. Closes at ${crossing.closureStage}ft.`,
    geometry:     { type: 'point', lat: crossing.lat, lon: crossing.lon },
    roadName:     crossing.name,
    floodRisk:    flooded ? 'extreme' : 'high',
    usgsGaugeId:  crossing.gaugeId,
    usgsGaugeFt:  gaugeFt,
    reportedAt:   nowISO(),
  });
}

// incidentFromNWS: derives an incident from an NWS evacuation/flood Alert
export function incidentFromNWS(alert) {
  const isEvac  = /evacu/i.test(alert.title ?? '');
  const isFlood = /flood/i.test(alert.title ?? '');
  const type    = isEvac  ? 'evacuation_order'
                : isFlood ? 'road_flooding'
                : 'other';

  return createIncident({
    incidentId:       'nws-' + alert.id,
    type,
    status:           alert.status === 'expired' ? 'resolved' : 'active',
    source:           'nws_derived',
    severity:         alert.severity === 'extreme' ? 5
                    : alert.severity === 'severe'  ? 4 : 3,
    title:            alert.title,
    description:      alert.description,
    affectedAreas:    alert.parishes ?? [],
    evacuationStatus: isEvac ? 'mandatory' : 'none',
    evacuationZones:  alert.parishes ?? [],
    nwsAlertId:       alert.id,
    reportedAt:       alert.issuedAt ?? nowISO(),
  });
}

// escalateFromReport: UserReport → Incident when confirmations pass threshold
export function escalateFromReport(report, confirmThreshold = 3) {
  if ((report.confirmCount ?? 0) < confirmThreshold) return null;

  const typeMap = {
    flood:  // sourceTier field uses SOURCE_TIER enum from Alert.js
  'road_flooding',
    road:   'road_closure',
    power:  'power_outage',
    fire:   'structure_fire',
    hazmat: 'hazmat',
    sos:    'other',
  };

  return createIncident({
    incidentId:    'report-' + report.id,
    type:          typeMap[report.type] ?? 'other',
    status:        'unverified',
    source:        'community',
    severity:      report.severity,
    title:         report.body?.substring(0, 120) ?? 'Community Report',
    description:   report.body,
    geometry:      report.lat ? { type: 'point', lat: report.lat, lon: report.lon } : null,
    zone:          report.zone,
    parish:        report.parish,
    confirmCount:  report.confirmCount,
    originReportId:report.id,
    reportedAt:    report.createdAt,
  });
}

// ── Firestore converter ────────────────────────────────────────────
const INCIDENT_FS_KEYS = [
  'incidentId','type','status','source','severity','title','description',
  'geometry','affectedAreas','parish','zone',
  'roadName','crossStreet','direction','lanesClosed','lanesTotal','isBothDirections',
  'floodRisk','waterDepthIn','usgsGaugeId','usgsGaugeFt',
  'estimatedCustomers','estimatedRestoreAt',
  'evacuationStatus','evacuationZones','evacuationOrder',
  'confidence','confidenceLevel','confidenceLabel','evidenceLabels','isStale','isDisputed','scoredAt',
  'confirmCount','denyCount','resourceCount','resourceTypes',
  'originReportId','dotdIncidentId','nwsAlertId','entergyIncidentId',
  'reportedAt','updatedAt','resolvedAt','estimatedDuration','geohash',
];

const INCIDENT_UPDATE_FS_KEYS = [
  'updateId','incidentId','authorId','authorName','isOfficial',
  'text','statusChange','evacuationChange','severityChange','newWaterDepthIn',
  'mediaUrls','createdAt',
];

const INCIDENT_MIGRATIONS = {
  // v0 used 'id' instead of 'incidentId'
  0: (d) => {
    if (d.id && !d.incidentId) { d.incidentId = d.id; delete d.id; }
    // v0 geometry was flat lat/lon fields
    if (!d.geometry && d.lat && d.lon) {
      d.geometry = { type: 'point', lat: d.lat, lon: d.lon };
    }
    return d;
  },
};

export const IncidentConverter = {
  toFirestore:   (i) => toFirestoreClean(i, INCIDENT_FS_KEYS),
  fromFirestore: (snap) => fromFirestoreNormalize(snap.data(), createIncident, INCIDENT_MIGRATIONS),
};

export const IncidentUpdateConverter = {
  toFirestore:   (u) => toFirestoreClean(u, INCIDENT_UPDATE_FS_KEYS),
  fromFirestore: (snap) => fromFirestoreNormalize(snap.data(), (d) => createIncidentUpdate(d.incidentId, d), {}),
};

// ── Helpers ────────────────────────────────────────────────────────
function _defaultSeverity(type) {
  const map = {
    evacuation_order:   5,
    structure_fire:     5,
    gas_leak:           5,
    hazmat:             5,
    levee_concern:      5,
    evacuation_warning: 4,
    road_flooding:      4,
    power_outage:       3,
    bridge_closure:     3,
    road_closure:       3,
    water_main:         3,
    pump_station:       3,
    debris:             2,
    accident:           2,
    curfew:             2,
    shelter_activation: 2,
    other:              1,
  };
  return map[type] ?? 1;
}

function _extractAreas(event) {
  const areas = [];
  if (event.County   || event.county)   areas.push(event.County   ?? event.county);
  if (event.Location || event.location) areas.push(event.Location ?? event.location);
  return [...new Set(areas.filter(Boolean))];
}

function _extractParish511(event) {
  const raw = (event.County ?? event.county ?? event.Area ?? event.area ?? '').toLowerCase();
  const MAP = {
    'orleans':    'Orleans',    'jefferson':  'Jefferson',
    'st. bernard':'St. Bernard','st bernard': 'St. Bernard',
    'plaquemines':'Plaquemines','st. tammany':'St. Tammany',
    'st tammany': 'St. Tammany','st. charles':'St. Charles',
    'st charles': 'St. Charles','st. john':   'St. John',
    'st john':    'St. John',
  };
  return MAP[raw.replace(/\s*parish\s*/i, '').trim()] ?? null;
}
